import { useState, useEffect, useCallback, useMemo } from 'react';
import { ProductMovementHistoryService } from '../../../api/services/ProductMovementHistoryService';
import { LocationsService } from '../../../api/services/LocationsService';
import { ProductHistoryListResponseDto } from '../../../api/models/ProductHistoryListResponseDto';
import { ProductHistoryDetailResponseDto } from '../../../api/models/ProductHistoryDetailResponseDto';
import { ProductHistoryFilterState, ProductHistorySort } from '../../../types/productHistory.types';
import { Location } from '../../../api/models/Location';
import { User } from '../../../api/models/User';
import { UsersServiceHelpers } from '../../../utils/api-helpers/UsersService.helpers';

/**
 * Hook for fetching product movement history data
 */
export const useProductMovementHistory = (
  filters: ProductHistoryFilterState,
  pagination: { page: number; limit: number },
  sort: ProductHistorySort,
) => {
  const [data, setData] = useState<ProductHistoryListResponseDto[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await ProductMovementHistoryService.productMovementHistoryControllerSearch(
        undefined, // productId - eliminado
        undefined, // warehouseProductId - eliminado
        undefined, // productCode - eliminado
        filters.serialNumber &&
          typeof filters.serialNumber === 'string' &&
          filters.serialNumber.trim() !== ''
          ? filters.serialNumber.trim()
          : undefined,
        filters.eventType,
        filters.startDate &&
          typeof filters.startDate === 'string' &&
          filters.startDate.trim() !== ''
          ? filters.startDate.trim()
          : undefined,
        filters.endDate && typeof filters.endDate === 'string' && filters.endDate.trim() !== ''
          ? filters.endDate.trim()
          : undefined,
        filters.userId && typeof filters.userId === 'string' && filters.userId.trim() !== ''
          ? filters.userId.trim()
          : undefined, // userId - solo enviar si tiene valor
        filters.locationId,
        filters.relatedEntityType &&
          typeof filters.relatedEntityType === 'string' &&
          filters.relatedEntityType.trim() !== ''
          ? filters.relatedEntityType.trim()
          : undefined,
        undefined, // relatedEntityId - eliminado
        filters.transferCode &&
          typeof filters.transferCode === 'string' &&
          filters.transferCode.trim() !== ''
          ? filters.transferCode.trim()
          : undefined,
        filters.orderCode &&
          typeof filters.orderCode === 'string' &&
          filters.orderCode.trim() !== ''
          ? filters.orderCode.trim()
          : undefined,
        filters.recordCode &&
          typeof filters.recordCode === 'string' &&
          filters.recordCode.trim() !== ''
          ? filters.recordCode.trim()
          : undefined,
        filters.search && typeof filters.search === 'string' && filters.search.trim() !== ''
          ? filters.search.trim()
          : undefined,
        pagination.page,
        pagination.limit,
        sort.sortBy,
        sort.sortOrder,
      );

      setData(response.data || []);
      setMeta(response.meta || {});
    } catch (err: any) {
      console.error('Error fetching product movement history:', err);
      setError(err?.message || 'Error al cargar el historial');
      setData([]);
      setMeta({});
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination, sort]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    data,
    meta,
    isLoading,
    error,
    refetch: fetchHistory,
  };
};

/**
 * Hook for fetching a single history record detail
 */
export const useProductMovementHistoryDetail = (id: number | null) => {
  const [data, setData] = useState<ProductHistoryDetailResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response =
        await ProductMovementHistoryService.productMovementHistoryControllerGetDetail(id);
      setData(response);
    } catch (err: any) {
      console.error('Error fetching history detail:', err);
      setError(err?.message || 'Error al cargar los detalles');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchDetail,
  };
};

/**
 * Hook for fetching locations
 */
export const useLocations = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await LocationsService.locationsControllerFindAll();

      // Handle different response formats
      let locationsList: Location[] = [];
      if (response && Array.isArray(response.data)) {
        locationsList = response.data as Location[];
      } else if (response && Array.isArray(response)) {
        locationsList = response as Location[];
      }

      setLocations(locationsList);
    } catch (err: any) {
      console.error('Error fetching locations:', err);
      setError(err?.message || 'Error al cargar las ubicaciones');
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  return {
    locations,
    isLoading,
    error,
    refetch: fetchLocations,
  };
};

/**
 * Hook for fetching users (filter dropdown).
 * Only call when the viewer can list users (`settings.users-management.view`);
 * report-only roles cannot hit GET /users.
 */
export const useUsers = (enabled: boolean = true) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!enabled) {
      setUsers([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await UsersServiceHelpers.usersControllerFindAllWithFilters(1, 500, {
        searchTerm: '',
        status: 'all',
      });

      let usersList: User[] = [];
      if (response && Array.isArray(response.data)) {
        usersList = response.data as User[];
      } else if (response && Array.isArray(response)) {
        usersList = response as User[];
      }

      usersList = [...usersList].sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
      });

      setUsers(usersList);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err?.message || 'Error al cargar los usuarios');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    isLoading,
    error,
    refetch: fetchUsers,
  };
};

/**
 * Hook for managing filter state
 */
export const useProductMovementHistoryFilters = (
  onFilterChange: (filters: ProductHistoryFilterState) => void,
  initialFilters?: ProductHistoryFilterState,
) => {
  const defaultFilters: ProductHistoryFilterState = {
    search: '',
    eventType: undefined,
    serialNumber: '',
    startDate: '',
    endDate: '',
    locationId: undefined,
    relatedEntityType: '',
    transferCode: '',
    orderCode: '',
    recordCode: '',
    productId: undefined,
    warehouseProductId: undefined,
    userId: '',
    relatedEntityId: undefined,
  };

  const [filters, setFilters] = useState<ProductHistoryFilterState>(
    initialFilters || defaultFilters,
  );
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters);
    }
  }, [initialFilters]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Handle different field types
    let processedValue: string | number | undefined;

    // Handle userId - convert empty string to undefined
    if (name === 'userId') {
      processedValue = value && value.trim() !== '' ? value.trim() : undefined;
    }
    // Handle number fields (aunque ya no se usan, mantener por compatibilidad)
    else if (name === 'productId' || name === 'warehouseProductId' || name === 'relatedEntityId') {
      if (value === '' || value === null || value === undefined) {
        processedValue = undefined;
      } else {
        const numValue = Number(value);
        processedValue = isNaN(numValue) ? undefined : numValue;
      }
    }
    // Handle string fields - convert empty to undefined
    else if (
      name === 'serialNumber' ||
      name === 'transferCode' ||
      name === 'orderCode' ||
      name === 'recordCode' ||
      name === 'search'
    ) {
      processedValue = value && value.trim() !== '' ? value.trim() : undefined;
    }
    // Handle date fields
    else if (name === 'startDate' || name === 'endDate') {
      processedValue = value && value.trim() !== '' ? value.trim() : undefined;
    }
    // Default: keep as is
    else {
      processedValue = value || undefined;
    }

    setFilters((prev) => {
      const newFilters = { ...prev, [name]: processedValue };

      // Validate dates when either date changes
      if (name === 'startDate' || name === 'endDate') {
        if (newFilters.startDate && newFilters.endDate) {
          const startDate = new Date(newFilters.startDate);
          const endDate = new Date(newFilters.endDate);
          if (startDate > endDate) {
            setDateError('La fecha de inicio debe ser anterior o igual a la fecha de fin');
          } else {
            setDateError(null);
          }
        } else {
          setDateError(null);
        }
      }

      return newFilters;
    });
  }, []);

  const handleSelectChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value === '' ? undefined : name === 'locationId' ? Number(value) : value,
    }));
    // Clear date error when other fields change
    setDateError(null);
  }, []);

  const handleEventTypeChange = useCallback(
    (eventType: ProductHistoryListResponseDto.eventType | '') => {
      setFilters((prev) => ({
        ...prev,
        eventType: eventType === '' ? undefined : eventType,
      }));
    },
    [],
  );

  const handleUserIdChange = useCallback((userId: string | null) => {
    setFilters((prev) => ({
      ...prev,
      userId: userId || undefined,
    }));
    setDateError(null);
  }, []);

  const handleReset = useCallback(() => {
    const resetFilters: ProductHistoryFilterState = {
      search: '',
      eventType: undefined,
      serialNumber: '',
      startDate: '',
      endDate: '',
      locationId: undefined,
      relatedEntityType: '',
      transferCode: '',
      orderCode: '',
      recordCode: '',
      productId: undefined,
      warehouseProductId: undefined,
      userId: '',
      relatedEntityId: undefined,
    };
    setFilters(resetFilters);
    setDateError(null);
    onFilterChange(resetFilters);
  }, [onFilterChange]);

  const handleApply = useCallback(() => {
    // Validate dates before applying
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      if (startDate > endDate) {
        setDateError('La fecha de inicio debe ser anterior o igual a la fecha de fin');
        return; // Don't apply filters if dates are invalid
      }
    }
    setDateError(null);
    onFilterChange(filters);
  }, [filters, onFilterChange]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.eventType) count++;
    if (filters.serialNumber) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.locationId) count++;
    if (filters.relatedEntityType) count++;
    if (filters.transferCode) count++;
    if (filters.orderCode) count++;
    if (filters.recordCode) count++;
    if (filters.productId) count++;
    if (filters.warehouseProductId) count++;
    if (filters.userId) count++;
    if (filters.relatedEntityId) count++;
    return count;
  }, [filters]);

  return {
    filters,
    setFilters,
    showMoreFilters,
    setShowMoreFilters,
    handleChange,
    handleSelectChange,
    handleEventTypeChange,
    handleUserIdChange,
    handleReset,
    handleApply,
    activeFiltersCount,
    dateError,
  };
};
